// What the canvas shows at one step of a replay: the position, the move that
// led to it, the recorded clocks, and a banner that names the result at the end.
import { t } from '../i18n/I18n.js';
import { findKing, isInCheck } from '../game/attacks.js';
import { animatedSquares } from '../render/PieceAnimator.js';
import type { ViewerRotation } from '../render/geometry.js';
import type { TurnBannerView } from '../render/overlay/TurnBanner.js';
import { resultTitle, sideLabel, terminationText } from '../scenes/GameOverHost.js';
import type { BoardFrame } from '../scenes/SceneRender.js';
import type { FrameInputs } from '../scenes/frameModel.js';
import type { ReplayController } from './ReplayController.js';
import { recordOutcome } from './record.js';
import type { GameRecord } from './types.js';

const IDLE_INTERACTION: BoardFrame['interaction'] = {
  hovered: null, legalTargets: [], drag: null, focus: null, hint: null, timeMs: 0,
};

/** The board faces the colour played on this device; White for hot-seat. */
export function replayRotation(record: GameRecord): ViewerRotation {
  return record.seed.localColor === 'b' ? 180 : 0;
}

function banner(controller: ReplayController): TurnBannerView {
  const state = controller.getState();
  if (!controller.isAtEnd()) return { text: t('game.banner.toMove', { side: sideLabel(state.turn) }), tone: 'turn', dot: state.turn };
  const record = controller.getRecord();
  const outcome = recordOutcome(record);
  const title = resultTitle({ outcome, perspective: record.seed.localColor });
  return {
    text: `${title} · ${terminationText(outcome.termination)}`,
    tone: outcome.winner === null ? 'draw' : 'win',
    dot: outcome.winner,
  };
}

export function buildReplayFrame(
  controller: ReplayController,
  rotation: ViewerRotation,
  animation: FrameInputs['animation'],
): BoardFrame {
  const record = controller.getRecord();
  const state = controller.getState();
  const board = state.position.board;
  const last = state.lastMove;
  const clocks = record.moves[controller.getStepIndex() - 1]?.clocks ?? null;
  // A resigned or drawn game leaves no glowing king behind; a mated one does.
  const showCheck = !controller.isAtEnd() || record.termination === 'checkmate';
  return {
    board,
    rotation,
    marks: {
      lastMove: last === undefined ? null : { from: last.from, to: last.to },
      selected: null,
      checkSquare: showCheck && isInCheck(board, state.turn) ? findKing(board, state.turn) : null,
    },
    interaction: IDLE_INTERACTION,
    animation: animation === null ? null : { frame: animation.frame, skip: animatedSquares(animation.plan) },
    banner: banner(controller),
    clocks: clocks === null ? null : { ...clocks, running: null },
  };
}
